// src/api/auth.ts
import type { Router } from 'vue-router';

import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  getIdToken,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  browserPopupRedirectResolver,
  browserLocalPersistence,
  setPersistence
} from 'firebase/auth';

import { useScraperStore } from '@/store/scraperStore';
import { useUserStore } from '@/store/userStore'; // Import setUserStore
import api from './axiosBase';

// Flag to prevent App.vue's onAuthStateChanged from calling joinTenant
// while a signup/login flow is in progress (the auth flow already handles
// tenant joining via the newtenantid extra).
export let authFlowInProgress = false;

/**
 * Check which auth providers a user signed up with via backend API
 * This uses Firebase Admin SDK which bypasses email enumeration protection
 */
async function checkAuthProviders(
  email: string
): Promise<{ exists: boolean; providers: string[] }> {
  try {
    const response = await api.get('/v2/auth/providers', {
      params: { email }
    });
    return {
      exists: response.data.exists || false,
      providers: response.data.providers || []
    };
  } catch (error) {
    console.error('Error checking auth providers:', error);
    return { exists: false, providers: [] };
  }
}

/**
 * Convert Firebase sign-in method to a user-friendly provider name
 */
function getProviderDisplayName(method: string): string {
  switch (method) {
    case 'google.com':
      return 'Google';
    case 'password':
      return 'email and password';
    case 'facebook.com':
      return 'Facebook';
    case 'twitter.com':
      return 'Twitter';
    case 'apple.com':
      return 'Apple';
    case 'github.com':
      return 'GitHub';
    default:
      return method;
  }
}

/**
 * Get existing sign-in methods for an email and return a helpful message.
 * Uses backend API with Firebase Admin SDK to bypass email enumeration protection.
 */
async function getExistingProviderMessage(email: string): Promise<string> {
  try {
    const { exists, providers } = await checkAuthProviders(email);

    if (!exists || providers.length === 0) {
      // User doesn't exist
      return 'No account found with this email. Please sign up first.';
    }

    const providerNames = providers.map(getProviderDisplayName);

    if (providerNames.length === 1) {
      return `This email is registered with ${providerNames[0]}. Please sign in using ${providerNames[0]} instead.`;
    } else {
      const lastProvider = providerNames.pop();
      return `This email is registered with ${providerNames.join(
        ', '
      )} or ${lastProvider}. Please sign in using one of those methods instead.`;
    }
  } catch (error) {
    console.error('Error fetching sign-in methods:', error);
    return 'Invalid credentials. If you signed up with Google, please use "Log in with Google" instead.';
  }
}

function getFirebaseErrorMessage(code: string): string {
  switch (code) {
    case 'auth/email-already-in-use':
      return 'This email is already associated with an account. Please log in instead.';
    case 'auth/invalid-email':
      return 'The email address is invalid. Please check and try again.';
    case 'auth/weak-password':
      return 'The password is too weak. Please choose a stronger password.';
    case 'auth/invalid-credential':
      return 'Invalid credentials. Please check your email and password.';
    case 'auth/missing-password':
      return 'The password is required. Please enter a password.';
    case 'auth/account-exists-with-different-credential':
      return 'An account already exists with this email but uses a different sign-in method.';
    default:
      return 'An unexpected error occurred. Please try again later.';
  }
}

function buildAuthExtras(extras: string[]): string[] {
  const authExtras = [...extras];
  const scraperStore = useScraperStore();
  const storedTenantId =
    scraperStore.newtenantid || localStorage.getItem('newtenantid');

  if (storedTenantId && !authExtras.includes(`newtenantid:${storedTenantId}`)) {
    authExtras.push(`newtenantid:${storedTenantId}`);
  }

  return authExtras;
}

/**
 * signUp
 * Attempts to create a new user. If email is already in use, tries to log in.
 * @param email
 * @param password
 * @param router
 * @param extras Additional strings to pass up (e.g. ["P1"]).
 * @returns { success: boolean, data?: any, message?: string }
 */
export const signUp = async (
  email: string,
  password: string,
  router: Router,
  extras: string[]
): Promise<{
  success: boolean;
  data?: Record<string, unknown>;
  message?: string;
}> => {
  const store = useScraperStore();
  const auth = getAuth();

  // Validate email and password before calling Firebase
  if (!email || !email.trim()) {
    return {
      success: false,
      message: 'Please enter your email address.'
    };
  }
  if (!password) {
    return {
      success: false,
      message: 'Please enter a password.'
    };
  }

  try {
    authFlowInProgress = true;

    // 1) Create a new user
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = userCredential.user;
    const idToken = await getIdToken(user);

    if (!store.deepAnalysisId && !extras.includes('P1')) {
      console.error('Deep Analysis ID not found');
      return {
        success: false,
        message: 'You must enter a URL to sign up.'
      };
    }

    // 2) Hit your agent/auth endpoint, but via your shared Axios instance:
    const response = await api.post(
      '/v2/agent/auth',
      {
        'DeepAnalysis-ID': store.deepAnalysisId,
        Extras: extras
      },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (response.status === 200) {
      console.log('Sign up successful');
      return { success: true, data: response.data };
    } else {
      console.error('Sign up failed');
      return {
        success: false,
        message: 'Sign up failed. Please try again.'
      };
    }
  } catch (error: unknown) {
    console.error('Error during sign up:', error);

    // If error is a FirebaseError and email is already in use, try to sign in with same credentials
    const firebaseError = error as { code: string };
    if (firebaseError?.code === 'auth/email-already-in-use') {
      console.log('Email already in use. Attempting login...');
      try {
        const userCredential = await signInWithEmailAndPassword(
          auth,
          email,
          password
        );
        const user = userCredential.user;
        const idToken = await getIdToken(user);

        const response = await api.post(
          '/v2/agent/auth',
          {
            'DeepAnalysis-ID': store.deepAnalysisId,
            Extras: extras
          },
          {
            headers: {
              Authorization: `Bearer ${idToken}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.status === 200) {
          console.log('response.data', response.data.Agent);
          console.log(response.data.Agent);
          useUserStore().setUser(response.data.Agent); // Set the user in the store
          console.log('Login successful (via sign-up fallback)');
          return { success: true, data: response.data };
        } else {
          console.error('Login failed (via sign-up fallback)');
          return {
            success: false,
            message: 'Email is already in use. Please Log In instead.'
          };
        }
      } catch (loginError: unknown) {
        console.error(
          'Login attempt failed after email already in use:',
          loginError
        );
        const loginFirebaseError = loginError as { code: string };
        if (loginFirebaseError?.code === 'auth/invalid-credential') {
          return {
            success: false,
            message: 'Email is already in use. Please Log In instead.'
          };
        }
        return {
          success: false,
          message: getFirebaseErrorMessage(
            loginFirebaseError?.code || 'unknown-error'
          )
        };
      }
    }

    // For other Firebase error codes (invalid-email, weak-password, etc.)
    const otherFirebaseError = error as { code: string };
    const msg = getFirebaseErrorMessage(
      otherFirebaseError?.code || 'unknown-error'
    );
    return {
      success: false,
      message: msg
    };
  } finally {
    authFlowInProgress = false;
  }
};

/**
 * login
 * Attempts to sign in with email/password.
 * @param email
 * @param password
 * @param extras Additional strings to pass up (e.g. ["P1"]).
 * @returns { success: boolean, data?: any, message?: string }
 */
export const login = async (
  email: string,
  password: string,
  extras: string[]
): Promise<{
  success: boolean;
  data?: Record<string, unknown>;
  message?: string;
}> => {
  const auth = getAuth();

  // Validate email and password before calling Firebase
  if (!email || !email.trim()) {
    return {
      success: false,
      message: 'Please enter your email address.'
    };
  }
  if (!password) {
    return {
      success: false,
      message: 'Please enter a password.'
    };
  }

  try {
    authFlowInProgress = true;

    // 1) Sign in user
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = userCredential.user;
    const idToken = await getIdToken(user);

    // 2) Call /v2/agent/auth using your shared Axios instance
    const response = await api.post(
      '/v2/agent/auth',
      {
        Extras: extras
      },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (response.status === 200) {
      console.log('response.data', response.data.Agent);
      console.log(response.data.Agent);
      useUserStore().setUser(response.data.Agent); // Set the user in the store
      return { success: true, data: response.data };
    } else {
      console.error('Login failed');
      return { success: false, message: 'Login failed. Please try again.' };
    }
  } catch (error: unknown) {
    console.error('Error during login:', error);
    const loginError = error as { code: string };

    // Check if the error is due to invalid credentials - might be because they used a different provider
    if (
      loginError?.code === 'auth/invalid-credential' ||
      loginError?.code === 'auth/wrong-password' ||
      loginError?.code === 'auth/user-not-found'
    ) {
      // Try to get existing providers for this email
      const providerMessage = await getExistingProviderMessage(email);
      return { success: false, message: providerMessage };
    }

    const msg = getFirebaseErrorMessage(loginError?.code || 'unknown-error');
    return { success: false, message: msg };
  } finally {
    authFlowInProgress = false;
  }
};

/**
 * signInWithGoogle
 * Handles Google authentication flow
 * Uses redirect flow for better cross-origin compatibility
 * @param extras Additional strings to pass up (e.g. ["P1"]).
 * @returns { success: boolean, data?: any, message?: string }
 */
export const signInWithGoogle = async (
  extras: string[]
): Promise<{
  success: boolean;
  data?: Record<string, unknown>;
  message?: string;
}> => {
  const auth = getAuth();
  const provider = new GoogleAuthProvider();
  const authExtras = buildAuthExtras(extras);

  try {
    authFlowInProgress = true;

    // Set persistence to local storage to avoid session state issues
    await setPersistence(auth, browserLocalPersistence);

    // Store extras in sessionStorage so we can retrieve them after redirect
    sessionStorage.setItem('googleAuthExtras', JSON.stringify(authExtras));

    // Try popup first (works well locally and in some browsers)
    try {
      const result = await signInWithPopup(
        auth,
        provider,
        browserPopupRedirectResolver
      );
      const user = result.user;
      const idToken = await getIdToken(user);

      // Clear stored extras
      sessionStorage.removeItem('googleAuthExtras');

      // Call /v2/agent/auth using your shared Axios instance
      const response = await api.post(
        '/v2/agent/auth',
        {
          Extras: authExtras
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200) {
        console.log('Google login successful');
        console.log('response.data', response.data.Agent);
        useUserStore().setUser(response.data.Agent);
        return { success: true, data: response.data };
      } else {
        console.error('Google login failed');
        return {
          success: false,
          message: 'Google login failed. Please try again.'
        };
      }
    } catch (popupError: unknown) {
      // Check if popup was closed or blocked - fall back to redirect
      const errorCode = (popupError as { code?: string })?.code;
      if (
        errorCode === 'auth/popup-closed-by-user' ||
        errorCode === 'auth/popup-blocked' ||
        errorCode === 'auth/cancelled-popup-request'
      ) {
        console.log('Popup failed, falling back to redirect flow...');
        // Use redirect flow as fallback
        await signInWithRedirect(auth, provider);
        // This won't return - the page will redirect
        return { success: true, message: 'Redirecting to Google...' };
      }
      throw popupError; // Re-throw other errors
    }
  } catch (error: unknown) {
    console.error('Error during Google login:', error);
    let message = 'An unexpected error occurred. Please try again later.';

    if (error && typeof error === 'object' && 'code' in error) {
      const errorWithCode = error as {
        code: string;
        customData?: { email?: string };
        message?: string;
      };
      if (errorWithCode.code === 'auth/popup-closed-by-user') {
        message = 'Login cancelled. Please try again.';
      } else if (
        errorWithCode.code === 'auth/account-exists-with-different-credential'
      ) {
        const email = errorWithCode.customData?.email;
        if (email) {
          message = await getExistingProviderMessage(email);
        } else {
          message =
            'An account already exists with this email but uses a different sign-in method. Try signing in with email and password instead.';
        }
      } else if (errorWithCode.code === 'auth/popup-blocked') {
        message =
          'Popup was blocked by your browser. Please allow popups for this site and try again.';
      } else if (errorWithCode.code === 'auth/cancelled-popup-request') {
        message = 'Login cancelled. Please try again.';
      } else if (
        errorWithCode.message?.includes('missing initial state') ||
        errorWithCode.code === 'auth/missing-initial-state'
      ) {
        message =
          'Browser session error. Please clear your browser cache/cookies for this site, or try a different browser.';
      }
    } else if (
      error &&
      typeof error === 'object' &&
      'message' in error &&
      typeof (error as { message: string }).message === 'string' &&
      (error as { message: string }).message.includes('missing initial state')
    ) {
      message =
        'Browser session error. Please clear your browser cache/cookies for this site, or try a different browser.';
    }

    return { success: false, message };
  } finally {
    authFlowInProgress = false;
  }
};

/**
 * Handle the redirect result after Google sign-in redirect
 * Call this on app initialization to check if user is returning from Google auth
 */
export const handleGoogleRedirectResult = async (
  extras?: string[]
): Promise<{
  success: boolean;
  data?: Record<string, unknown>;
  message?: string;
  handled: boolean;
}> => {
  const auth = getAuth();

  try {
    authFlowInProgress = true;

    const result = await getRedirectResult(auth);
    if (!result) {
      return { success: false, handled: false };
    }

    const user = result.user;
    const idToken = await getIdToken(user);

    // Retrieve stored extras or use provided ones
    const storedExtras = sessionStorage.getItem('googleAuthExtras');
    const baseExtras = extras || (storedExtras ? JSON.parse(storedExtras) : []);
    const authExtras = buildAuthExtras(baseExtras);
    sessionStorage.removeItem('googleAuthExtras');

    const response = await api.post(
      '/v2/agent/auth',
      {
        Extras: authExtras
      },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (response.status === 200) {
      console.log('Google redirect login successful');
      useUserStore().setUser(response.data.Agent);
      return { success: true, data: response.data, handled: true };
    } else {
      return {
        success: false,
        message: 'Google login failed. Please try again.',
        handled: true
      };
    }
  } catch (error) {
    console.error('Error handling Google redirect result:', error);
    return {
      success: false,
      message: 'An error occurred during sign-in.',
      handled: true
    };
  } finally {
    authFlowInProgress = false;
  }
};
